import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Download, Upload } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useIntl } from "react-intl";
import type { BulkUpdateCategoriesItem } from "~/api/model/bulkUpdateCategoriesItem";
import {
  getListOrganizationCategoriesQueryKey,
  getListOrganizationCategoryFoldersQueryKey,
  useBulkCategoriesUpdate,
  useListOrganizationCategories,
  useListOrganizationCategoryFolders,
} from "~/api/organization/organization";
import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "~/components/ui/dialog";
import { Label } from "~/components/ui/label";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { downloadAsCSV, parseCSV } from "~/utils/csv";
import logger from "~/utils/logger";

/**
 * Extracts error message from various error object structures.
 * Handles API errors with nested response.data.message structure.
 *
 * @param error - Error object from API call
 * @returns Error message string or undefined if not found
 */
function getErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;

  if (
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response &&
    error.response.data &&
    typeof error.response.data === "object" &&
    "message" in error.response.data
  ) {
    return String(error.response.data.message);
  }

  if ("message" in error) {
    return String(error.message);
  }

  return undefined;
}

/**
 * Extracts bulk category operation counts from API response.
 *
 * The API returns different counts depending on the mode:
 * - Replace All mode (deleteExisting=true): deleted > 0, created > 0, updated = 0, deletedFolders >= 0
 * - Add mode (deleteExisting=false): deleted = 0, created >= 0, updated >= 0, deletedFolders = 0
 *
 * The deletedFolders count indicates how many empty folders were cleaned up after the operation.
 * This only happens in Replace All mode when folders that previously had categories are no longer
 * referenced by any category in the imported CSV.
 *
 * @param raw - Raw response from bulkCategoriesUpdate mutation
 * @returns Object with created, updated, deleted, and deletedFolders counts
 */
function extractBulkCategoryCounts(raw: unknown): {
  created: number;
  updated: number;
  deleted: number;
  deletedFolders: number;
} {
  if (typeof raw === "object" && raw !== null) {
    const r = raw as Record<string, unknown>;
    return {
      created: typeof r.created === "number" ? r.created : 0,
      updated: typeof r.updated === "number" ? r.updated : 0,
      deleted: typeof r.deleted === "number" ? r.deleted : 0,
      deletedFolders:
        typeof r.deletedFolders === "number" ? r.deletedFolders : 0,
    };
  }
  return { created: 0, updated: 0, deleted: 0, deletedFolders: 0 };
}

export default function BulkUpdateModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: bulkCategoriesUpdate } = useBulkCategoriesUpdate();
  const { data: categories } = useListOrganizationCategories(organizationId);
  const { data: folders } = useListOrganizationCategoryFolders(organizationId);
  const { toast } = useToast();
  const intl = useIntl();

  const onDragEnter = () => {
    setIsDragActive(true);
  };

  const onDragLeave = () => {
    setIsDragActive(false);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter,
    onDragLeave,
    onDrop: (acceptedFiles) => {
      setIsDragActive(false);
      handleDrop(acceptedFiles);
    },
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const defaultCategory = categories?.find((category) => category.isFallback);
  const exportableCategories = categories?.filter(
    (category) => !category.isFallback
  );

  const handleDownloadCSV = () => {
    if (!exportableCategories?.length) return;

    const folderIdToNameMap = new Map(
      folders?.map((folder) => [folder.id, folder.name]) || []
    );

    const headers = [
      "name",
      "definition",
      "length",
      "keyTopics",
      "recognitionTips",
      "namingConvention",
      "expectedContent",
      "namingExample",
      "folderName",
    ];

    const rows = exportableCategories.map((category) => [
      category.name,
      category.definition || "",
      category.length || "",
      category.keyTopics || "",
      category.recognitionTips || "",
      category.namingConvention || "",
      category.expectedContent || "",
      category.namingExample || "",
      category.folderId ? folderIdToNameMap.get(category.folderId) || "" : "",
    ]);

    downloadAsCSV(`categories.csv`, headers, rows);
  };

  const handleUpload = async () => {
    if (!selectedFile || isUploading) return;

    setIsUploading(true);
    try {
      const text = await selectedFile.text();
      const parsed = parseCSV<BulkUpdateCategoriesItem>(text);

      const defaultCategoryName = defaultCategory?.name;
      const normalizedDefaultName = defaultCategoryName?.trim().toLowerCase();

      const categoriesToUpload =
        deleteExisting && normalizedDefaultName
          ? parsed.map((item) => {
              const itemName = item.name?.trim();
              if (
                itemName &&
                itemName.toLowerCase() === normalizedDefaultName
              ) {
                return {
                  ...item,
                  name: `${itemName} (imported)`,
                };
              }
              return item;
            })
          : parsed;

      const raw = (await bulkCategoriesUpdate({
        organizationId,
        data: {
          categories: categoriesToUpload,
          deleteExisting,
        },
      })) as unknown;

      const counts = extractBulkCategoryCounts(raw);

      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoriesQueryKey(organizationId),
      });

      queryClient.invalidateQueries({
        queryKey: getListOrganizationCategoryFoldersQueryKey(organizationId),
      });

      toast({
        title: intl.formatMessage({ id: "success" }),
        description: intl.formatMessage(
          { id: "settings.bulkUpdate.successMessage" },
          {
            created: counts.created,
            updated: counts.updated,
            deleted: counts.deleted,
            deletedFolders: counts.deletedFolders,
          }
        ),
      });
      onOpenChange(false);
      setSelectedFile(null);
      setDeleteExisting(false);
    } catch (error: unknown) {
      logger.error("Error uploading categories:", error);

      const errorMessage = getErrorMessage(error);

      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "error" }),
        description:
          errorMessage ||
          intl.formatMessage({ id: "settings.bulkUpdate.errorMessage" }),
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle>
          {intl.formatMessage({ id: "settings.bulkUpdate.title" })}
        </DialogTitle>

        <DialogDescription>
          {intl.formatMessage({ id: "settings.bulkUpdate.description" })}
        </DialogDescription>

        <div className="space-y-6">
          <div>
            <h3 className="font-medium mb-3">
              {intl.formatMessage({ id: "settings.bulkUpdate.exportStep" })}
            </h3>
            <Button variant="outline" size="sm" onClick={handleDownloadCSV}>
              <Download className="w-4 h-4 mr-2" />
              {intl.formatMessage(
                { id: "settings.bulkUpdate.exportButton" },
                { count: exportableCategories?.length || 0 }
              )}
            </Button>
          </div>

          <div>
            <h3 className="font-medium mb-3">
              {intl.formatMessage({ id: "settings.bulkUpdate.editStep" })}
            </h3>
          </div>

          <div>
            <h3 className="font-medium mb-3">
              {intl.formatMessage({ id: "settings.bulkUpdate.importStep" })}
            </h3>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deleteExisting"
                  checked={deleteExisting}
                  onCheckedChange={(checked) =>
                    setDeleteExisting(checked === true)
                  }
                />
                <Label
                  htmlFor="deleteExisting"
                  className="text-sm font-normal cursor-pointer"
                >
                  {intl.formatMessage({
                    id: "settings.bulkUpdate.deleteExisting",
                  })}
                </Label>
              </div>

              <p
                className={clsx("text-sm font-medium", {
                  "text-red-500": deleteExisting,
                  "text-gray-600": !deleteExisting,
                })}
              >
                {intl.formatMessage({
                  id: deleteExisting
                    ? "settings.bulkUpdate.warningReplace"
                    : "settings.bulkUpdate.infoAdd",
                })}
              </p>

              <div
                {...getRootProps()}
                className={clsx(
                  "border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors",
                  {
                    "border-blue-500 bg-blue-50": isDragActive,
                    "border-gray-300 hover:border-gray-400": !isDragActive,
                  }
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-background h-12 w-12 rounded-full border border-border flex items-center justify-center">
                    <Upload className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                      {selectedFile
                        ? selectedFile.name
                        : intl.formatMessage({
                            id: "settings.bulkUpdate.dragAndDrop",
                          })}
                    </p>
                    <Button variant="secondary" size="sm" type="button">
                      {intl.formatMessage({
                        id: "settings.bulkUpdate.chooseFile",
                      })}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUploading}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
          >
            {isUploading
              ? intl.formatMessage({ id: "settings.bulkUpdate.processing" })
              : intl.formatMessage({ id: "continue" })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
